import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Form, Input, Select, Spin, Modal } from 'antd';
import {
	ArrowLeftOutlined,
	EditOutlined,
	DeleteOutlined,
	LockOutlined,
} from '@ant-design/icons';
import { Button } from '../../../../components/ui';
import {
	createUser,
	getUser,
	updateUser,
	deleteUser,
	resetUserPassword,
	userKeys,
} from '../../../../api/user.api';
import { getRoles, roleKeys } from '../../../../api/role.api';
import { toast, handleMutationError } from '../../../../lib/notification';
import { optionalPhoneValidator } from '../../../../lib/phone-validation';
import type { CreateUserPayload, UpdateUserPayload } from '../../../../types/iam-user.types';

type FormMode = 'create' | 'view' | 'edit';

function useUserFormMode(): { mode: FormMode; userId: string | null } {
	const { pathname } = useLocation();

	return useMemo(() => {
		if (pathname.endsWith('/create')) {
			return { mode: 'create' as const, userId: null };
		}

		const segments = pathname.split('/');
		const last = segments[segments.length - 1]!;
		if (last === 'edit') {
			return { mode: 'edit' as const, userId: segments[segments.length - 2] ?? null };
		}

		return { mode: 'view' as const, userId: last };
	}, [pathname]);
}

interface UserFormViewProps {
	editPermission?: string;
	deletePermission?: string;
	resetPasswordPermission?: string;
}

export default function UserFormView({
	editPermission = 'iam:user:update',
	deletePermission = 'iam:user:delete',
	resetPasswordPermission = 'iam:user:reset_password',
}: UserFormViewProps = {}) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { mode, userId } = useUserFormMode();
	const [form] = Form.useForm();
	const isReadOnly = mode === 'view';

	const { data: userDetail, isLoading: isUserLoading } = useQuery({
		queryKey: userKeys.detail(userId!),
		queryFn: () => getUser(userId!),
		enabled: !!userId,
	});

	const { data: rolesData } = useQuery({
		queryKey: roleKeys.list(),
		queryFn: () => getRoles(),
	});

	const roleOptions = useMemo(
		() => (rolesData?.items || []).map((r) => ({ label: r.title, value: r.id })),
		[rolesData]
	);

	useEffect(() => {
		if (userDetail) {
			form.setFieldsValue({
				firstName: userDetail.firstName,
				lastName: userDetail.lastName,
				userName: userDetail.userName,
				email: userDetail.email,
				phoneNumber: userDetail.phoneNumber,
				roles: userDetail.roles?.map((r) => r.value),
			});
		}
	}, [userDetail, form]);

	const createMutation = useMutation({
		mutationFn: createUser,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: userKeys.lists() });
			toast.success('User created successfully');
			navigate('/iam/users');
		},
		onError: (error: unknown) => {
			handleMutationError(error, 'Failed to create user');
		},
	});

	const updateMutation = useMutation({
		mutationFn: (payload: UpdateUserPayload) => updateUser(userId!, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: userKeys.lists() });
			queryClient.invalidateQueries({ queryKey: userKeys.detail(userId!) });
			toast.success('User updated successfully');
			navigate(`/iam/users/${userId}`);
		},
		onError: (error: unknown) => {
			handleMutationError(error, 'Failed to update user');
		},
	});

	const deleteMutation = useMutation({
		mutationFn: () => deleteUser(userId!),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: userKeys.lists() });
			toast.success('User deleted successfully');
			navigate('/iam/users');
		},
		onError: (error: unknown) => {
			handleMutationError(error, 'Failed to delete user');
		},
	});

	const resetPasswordMutation = useMutation({
		mutationFn: () => resetUserPassword(userId!),
		onSuccess: () => {
			toast.success('Password reset initiated successfully');
		},
		onError: (error: unknown) => {
			handleMutationError(error, 'Failed to reset password');
		},
	});

	const handleSubmit = (values: Record<string, unknown>) => {
		const roles = (values.roles as string[]) || [];

		if (mode === 'create') {
			const payload: CreateUserPayload = {
				firstName: values.firstName as string,
				lastName: values.lastName as string,
				userName: values.userName as string,
				email: values.email as string,
				phoneNumber: (values.phoneNumber as string) || undefined,
				roles,
			};
			createMutation.mutate(payload);
		} else {
			const payload: UpdateUserPayload = {
				firstName: values.firstName as string,
				lastName: values.lastName as string,
				userName: values.userName as string,
				email: values.email as string,
				phoneNumber: (values.phoneNumber as string) || undefined,
				status: userDetail?.status,
				roles,
			};
			updateMutation.mutate(payload);
		}
	};

	const handleDelete = () => {
		Modal.confirm({
			title: 'Delete User',
			content: 'Are you sure you want to delete this user? This action cannot be undone.',
			okText: 'Delete',
			okType: 'danger',
			onOk: () => deleteMutation.mutate(),
		});
	};

	const handleResetPassword = () => {
		Modal.confirm({
			title: 'Reset Password',
			content: 'Are you sure you want to reset this user\'s password?',
			okText: 'Reset Password',
			onOk: () => resetPasswordMutation.mutate(),
		});
	};

	const isSaving = createMutation.isPending || updateMutation.isPending;

	const pageTitle =
		mode === 'create' ? 'Create User' : mode === 'edit' ? 'Edit User' : 'User Details';

	if (userId && isUserLoading) {
		return (
			<div className="flex justify-center py-24">
				<Spin size="large" />
			</div>
		);
	}

	return (
		<div>
			<div className="flex items-center justify-between mb-6">
				<Button
					variant="text"
					icon={<ArrowLeftOutlined />}
					onClick={() => navigate('/iam/users')}
					size="small"
				>
					Back to Users
				</Button>

				{isReadOnly && (
					<div className="flex gap-2">
						<Button
							variant="secondary"
							icon={<LockOutlined />}
							permission={resetPasswordPermission}
							onClick={handleResetPassword}
							loading={resetPasswordMutation.isPending}
						>
							Reset Password
						</Button>
						<Button
							variant="primary"
							icon={<EditOutlined />}
							permission={editPermission}
							onClick={() => navigate(`/iam/users/${userId}/edit`)}
						>
							Edit
						</Button>
						<Button
							variant="secondary"
							icon={<DeleteOutlined />}
							permission={deletePermission}
							onClick={handleDelete}
							loading={deleteMutation.isPending}
						>
							Delete
						</Button>
					</div>
				)}
			</div>

			<h2 className="text-lg font-semibold text-gray-900 mb-6">{pageTitle}</h2>

			<Form form={form} layout="vertical" onFinish={handleSubmit}>
				<div className="grid grid-cols-2 gap-6 max-w-3xl">
					<Form.Item
						name="firstName"
						label="First Name"
						rules={[
							{ required: true, message: 'Please enter first name' },
							{ max: 200, message: 'First name must not exceed 200 characters' },
						]}
					>
						<Input placeholder="First Name" size="large" disabled={isReadOnly} />
					</Form.Item>

					<Form.Item
						name="lastName"
						label="Last Name"
						rules={[
							{ required: true, message: 'Please enter last name' },
							{ max: 200, message: 'Last name must not exceed 200 characters' },
						]}
					>
						<Input placeholder="Last Name" size="large" disabled={isReadOnly} />
					</Form.Item>

					<Form.Item
						name="userName"
						label="Username"
						rules={[
							{ required: true, message: 'Please enter a username' },
							{ max: 200, message: 'Username must not exceed 200 characters' },
						]}
					>
						<Input placeholder="Username" size="large" disabled={isReadOnly} />
					</Form.Item>

					<Form.Item
						name="email"
						label="Email"
						rules={[
							{ required: true, message: 'Please enter an email' },
							{ type: 'email', message: 'Please enter a valid email' },
						]}
					>
						<Input placeholder="john@example.com" size="large" disabled={isReadOnly} />
					</Form.Item>

					<Form.Item
						name="phoneNumber"
						label="Phone Number"
						rules={[{ validator: optionalPhoneValidator }]}
					>
						<Input placeholder="+1234567890" size="large" disabled={isReadOnly} />
					</Form.Item>

					<Form.Item
						name="roles"
						label="Roles"
						className="col-span-2"
						rules={[{ required: true, message: 'At least one role is required' }]}
					>
						<Select
							mode="multiple"
							placeholder="Select roles"
							size="large"
							disabled={isReadOnly}
							options={roleOptions}
						/>
					</Form.Item>
				</div>

				{!isReadOnly && (
					<div className="flex justify-end gap-3 pt-4">
						<Button variant="secondary" onClick={() => navigate('/iam/users')}>
							Cancel
						</Button>
						<Button variant="primary" htmlType="submit" loading={isSaving}>
							{mode === 'edit' ? 'Update User' : 'Create User'}
						</Button>
					</div>
				)}
			</Form>
		</div>
	);
}
